import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFirebase, useFirestore } from 'react-redux-firebase';
import axios from 'axios';
import propTypes from 'prop-types';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { actions } from '../../../../actions';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Chip,
  Grid,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  UploadFile as UploadFileIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { generateEmailBody, generateEmailTemplate } from './generateEmailHtmlContent';
import EmailInput from './EmailInput';
import EmailConfirmationButtons from './EmailConfirmationButtons';
import EmailConfig from './EmailConfig';
import { getCombinedBill } from './getCombinedBill';

const administrationEmail = process.env.REACT_APP_ADMINISTRATION_EMAIL;

const maruCCEmail = 'mmarangoni@prestobots.com'; // TODO: ver como guardar este correo, es muy particular

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  justifyContent: 'space-around',
  background: 'rgba(255, 255, 255, 1)',
  border: '2px solid #ccc',
  borderRadius: 8,
  boxShadow: 24,
  p: 4,
  m: 0,
  display: 'flex',
  alignItems: 'flex-start',
  maxHeight: '90dvh',
  overflowY: 'auto',
};

const scrollbarStyles = {
  maxHeight: '80dvh', // Adjust as needed
  overflowY: 'auto', // Enable vertical scrolling
  '&::-webkit-scrollbar': {
    width: '8px', // Ancho de la barra de desplazamiento
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1', // Color de fondo del track
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888', // Color de la thumb
    borderRadius: '10px', // Bordes redondeados
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555', // Color de la thumb al pasar el ratón
  },
  scrollbarWidth: 'thin', // Estilo del ancho de la barra de desplazamiento en Firefox
  scrollbarColor: '#888 #f1f1f1', // Color de la thumb y del track en Firefox
};

const ModalBillEmail = ({ modalOpen, setModalOpen, rows, prepago, subTotal, billDate }) => {
  const uploadBillRef = useRef(null);
  const firebase = useFirebase();
  const firestore = useFirestore();
  const appDispatch = useDispatch();
  const organization = useSelector((state) => state.app.organization);
  const profile = useSelector((state) => state.firebase.profile);
  const ref = firebase.storage().ref();

  const memorizedValues = useMemo(() => {
    const billingEmails = organization?.configuration?.billingEmails;

    return {
      bankAccount: billingEmails?.bankAccount || {},
      emails: billingEmails?.emails || [],
      frequentEmails: billingEmails?.frequentEmails || [],
      personalizedText: billingEmails?.personalizedText || '',
      combinedBillOrg: billingEmails?.combinedBillOrg || {},
    };
  }, [organization?.configuration?.billingEmails]);

  const { bankAccount, emails, frequentEmails, personalizedText, combinedBillOrg } =
    memorizedValues;

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);

  const [isEmailEnabled, setIsEmailEnabled] = useState(false);

  const [recipients, setRecipients] = useState(frequentEmails);
  const [ccRecipients, setCcRecipients] = useState([maruCCEmail]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [htmlContentPreview, sethtmlContentPreview] = useState();
  const [combinedBill, setCombinedBill] = useState({});

  const [errorMessages, setErrorMessages] = useState([]);

  const monthName = new Date(billDate).toLocaleString('es-ES', { month: 'long' });

  useEffect(() => {
    setRecipients(frequentEmails);
    setCcRecipients([maruCCEmail]);
    setUploadFiles([]);
  }, [organization.id]);

  useEffect(() => {
    const validateEmailData = () => {
      const errors = [];
      try {
        if (uploadFiles.length === 0) {
          errors.push('Debe cargar una factura.');
        }

        const allRecipients = [...recipients, ...ccRecipients, administrationEmail];
        const hasDuplicates = new Set(allRecipients).size !== allRecipients.length;

        if (hasDuplicates) {
          errors.push('Hay direcciones de correo electrónico duplicadas.');
        }
        if (recipients.length === 0) {
          errors.push('Debe agregar al menos un destinatario.');
        }
        if (isEmpty(bankAccount)) {
          errors.push('Debe preseleccionar un banco.');
        }
      } catch (error) {
        console.error('validateEmailData', error);
        errors.push('Ha ocurrido un error, recargue la pagina por favor');
        setErrorMessages(errors);
        setIsEmailEnabled(errors.length === 0);
      }

      setErrorMessages(errors);
      setIsEmailEnabled(errors.length === 0);
    };

    validateEmailData();
  }, [recipients, ccRecipients, uploadFiles]);
  useEffect(() => {
    const callFetchAndCombineBills = async () => {
      if (!isEmpty(combinedBillOrg)) {
        const combinedBillResponse = await getCombinedBill(combinedBillOrg, billDate, firestore);
        setCombinedBill(combinedBillResponse);
      }
    };
    callFetchAndCombineBills();
  }, [combinedBillOrg, billDate]);

  const setSnackBarAlert = (message, severity) => {
    return appDispatch({
      type: actions.alert,
      alert: {
        snackbarOpen: true,
        snackbarMessage: message,
        snackbarSeverity: severity,
      },
    });
  };
  const setChargingModal = (boolean) => {
    return appDispatch({
      type: actions.changeLoading,
      loading: { loading: boolean, priority: 1 },
    });
  };

  const handleAddGeneric = (value, action = 'add', type) => {
    if (type === 'TO') {
      if (action === 'add') {
        setRecipients([...recipients, value]);
      } else {
        setRecipients(recipients.filter((item) => item !== value));
      }
    } else if (type === 'CC') {
      if (action === 'add') {
        setCcRecipients([...ccRecipients, value]);
      } else {
        setCcRecipients(ccRecipients.filter((item) => item !== value));
      }
    }
  };

  const handleUploadFileBilling = async (e) => {
    try {
      if (!e || !e.target || !e.target.files || e.target.files.length === 0) return;
      const file = e.target.files[0];

      if (file.type.toLowerCase().indexOf('pdf') === -1) {
        return setSnackBarAlert('Solo se aceptan archivos tipo PDF', 'error');
      }

      setChargingModal(true);

      const timestamp = moment().format('YYYYMMDDHHmmss');
      const fileName = `${timestamp}-${file.name}`;
      const route = `bills/${organization.id}/${fileName}`;
      const fileRef = ref.child(route);

      await fileRef.put(file);

      const fileURL = await fileRef.getDownloadURL();

      setUploadFiles([...uploadFiles, { name: file.name, url: fileURL, fileRef: fileName }]);
    } catch (error) {
      setSnackBarAlert('Error al subir el archivo. Por favor, inténtelo de nuevo.', 'error');
      console.error('uploadFileBilling', error);
    } finally {
      setChargingModal(false);
      uploadBillRef.current.value = '';
    }
  };

  const handleDeleteFileBilling = async (file) => {
    try {
      setChargingModal(true);
      const fileName = file.fileRef;
      const route = `bills/${organization.id}/${fileName}`;
      const fileRef = ref.child(route);

      await fileRef.delete();

      setUploadFiles((prevFilesNames) =>
        prevFilesNames.filter((item) => item.fileRef !== file.fileRef),
      );
      setSnackBarAlert(`Archivo eliminado exitosamente`, 'success');
    } catch (error) {
      setSnackBarAlert(`Error al eliminar el archivo: ${file.name}`, 'error');
      console.error('deleteFileBilling', error);
    } finally {
      setChargingModal(false);
    }
  };
  const handleSendEmail = async () => {
    try {
      setChargingModal(true);

      const capitalizedMonthName = monthName?.charAt(0).toUpperCase() + monthName.slice(1);
      const htmlContent = generateEmailBody({
        rows,
        subTotal,
        prepago,
        isAdmin: false,
        isEmailPreview: true,
        bankAccount,
        personalizedText,
        combinedBillOrg,
        combinedBill,
      });
      const formatedHtmlContent = generateEmailTemplate(htmlContent, capitalizedMonthName);
      const token = await firebase.auth().currentUser.getIdToken(true);
      const response = await axios.post(
        `${process.env.REACT_APP_EMAILS}`,
        {
          emailBody: formatedHtmlContent,
          emailRecipients: {
            recipients: recipients,
            ccRecipients: ccRecipients,
            bccRecipients: [administrationEmail],
          },
          emailAttachments: uploadFiles,
          month: capitalizedMonthName,
          organization: { id: organization.id, name: organization.name },
          requestedBy: profile.email,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `bearer ${token}`,
          },
        },
      );
      return response;
    } catch (error) {
      console.error('Error al enviar el correo:', error);
      return { status: 'error' };
    } finally {
      setChargingModal(false);
    }
  };

  return (
    <Modal
      open={modalOpen}
      onClose={() => {
        setPreviewModalOpen(false);
        setConfigModalOpen(false);
        setModalOpen(false);
      }}
    >
      <Box sx={style}>
        <IconButton
          onClick={() => {
            setPreviewModalOpen(false);
            setConfigModalOpen(false);
            setModalOpen(false);
          }}
          sx={{ position: 'absolute', top: 6, right: 6 }}
        >
          <CloseIcon />
        </IconButton>
        <IconButton
          onClick={() => {
            setConfigModalOpen(!configModalOpen);
            !configModalOpen && setPreviewModalOpen(false);
          }}
          sx={{ position: 'absolute', top: 6, left: 6 }}
        >
          {!configModalOpen ? (
            <Tooltip title="Configuraciones" arrow>
              <SettingsIcon
                sx={{ color: 'primary.main', fontSize: 'inherit', transform: 'scale(1.2)' }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Volver al inicio" arrow>
              <ArrowBackIcon
                sx={{ color: 'secondary.main', fontSize: 'inherit', transform: 'scale(1.2)' }}
              />
            </Tooltip>
          )}
        </IconButton>
        {configModalOpen && (
          <EmailConfig
            organization={organization}
            scrollbarStyles={scrollbarStyles}
            previewModalOpen={previewModalOpen}
          />
        )}

        {!configModalOpen && (
          <Box
            sx={{
              width: !previewModalOpen ? '100%' : '400px',
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '535px',
              minWidth: '400px',
              padding: 1,
              height: '100%',
              ...scrollbarStyles,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Envío de facturación
            </Typography>

            <EmailInput
              label="Agregar destinatario"
              value={recipients}
              onAdd={(email, action) => handleAddGeneric(email, action, 'TO')}
              suggestions={emails}
            />

            <Divider sx={{ width: '100%', my: 2 }} />

            <EmailInput
              label="Agregar CC"
              value={ccRecipients}
              onAdd={(email, action) => handleAddGeneric(email, action, 'CC')}
              suggestions={emails}
            />

            <Divider sx={{ width: '100%', my: 2 }} />
            <Box display="flex" sx={{ width: '100%', alignItems: 'center', gap: '10px' }}>
              <Typography variant="subtitle1">CCO:</Typography>
              <Chip label={administrationEmail} color="secondary" sx={{ mr: 1, borderRadius: 4 }} />
            </Box>

            <Divider sx={{ width: '100%', my: 2 }} />
            <Box id="popup-btn" sx={{ position: 'relative', width: '100%' }}>
              <Grid container spacing={1}>
                {uploadFiles.map((file, index) => (
                  <Grid item key={index}>
                    <Chip
                      label={file.name}
                      onDelete={async () => handleDeleteFileBilling(file)}
                      color="success"
                      sx={{ mr: 1, borderRadius: 1 }}
                    />
                  </Grid>
                ))}
              </Grid>
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadFileIcon />}
                sx={{ mt: 2, width: '100%' }}
                onClick={() => uploadBillRef.current.click()}
              >
                Cargar Factura
              </Button>
              <input
                type="file"
                ref={uploadBillRef}
                onChange={handleUploadFileBilling}
                style={{ display: 'none' }}
              />
            </Box>
            <Button
              variant="contained"
              color="primary"
              sx={{ mt: 3 }}
              startIcon={previewModalOpen ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => {
                sethtmlContentPreview(
                  generateEmailTemplate(
                    generateEmailBody({
                      rows,
                      subTotal,
                      prepago,
                      isAdmin: false,
                      isEmailPreview: true,
                      bankAccount,
                      personalizedText,
                      combinedBillOrg,
                      combinedBill,
                    }),
                    monthName,
                  ),
                );
                setPreviewModalOpen(!previewModalOpen);
              }}
            >
              {previewModalOpen ? 'Ocultar vista previa' : 'Vista previa'}
            </Button>
            <EmailConfirmationButtons sendEmail={handleSendEmail} isEmailEnabled={isEmailEnabled} />
            {errorMessages.length > 0 && (
              <Box sx={{ color: 'error.main', mt: 1 }}>
                <Typography variant="body1" gutterBottom>
                  <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                  {errorMessages.join(' ')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {previewModalOpen && (
          <Box
            sx={{
              padding: '10px',
              border: '1px solid rgba(0, 0, 0, 0.12)',
              borderRadius: '8px',
              marginLeft: '20px',
              ...scrollbarStyles,
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Datos a facturar {monthName}
            </Typography>
            <div dangerouslySetInnerHTML={{ __html: htmlContentPreview }} />
          </Box>
        )}
      </Box>
    </Modal>
  );
};
const Props = {
  modalOpen: propTypes.bool,
  setModalOpen: propTypes.func.isRequired,
  rows: propTypes.array,
  subTotal: propTypes.number,
  prepago: propTypes.object,
  billDate: propTypes.any,
};

ModalBillEmail.propTypes = Props;

export default ModalBillEmail;
